import React, { useState } from 'react';
import { useSortableTable } from './hooks/useSortableTable';
import { apiFetch } from './utils/api';

function EmployeeVehiclesTab({ employeeId, vehicles, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  
  // Sorting
  const { items: sortedVehicles, requestSort, getSortIndicator } = useSortableTable(vehicles, { key: 'registration', direction: 'asc' });
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    registration: '',
    make: '',
    model: '',
    year: '',
    color: '',
    vehicle_type: 'Van',
    fuel_type: 'Diesel',
    mot_expiry: '',
    tax_expiry: '',
    service_due_date: '',
    service_due_mileage: '',
    current_mileage: '',
    allocated_date: new Date().toISOString().split('T')[0],
    return_date: '',
    insurance_policy: '',
    insurance_expiry: '',
    status: 'Active',
    notes: ''
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleAdd = () => {
    setEditingVehicle(null);
    setFormData({
      registration: '',
      make: '',
      model: '',
      year: '',
      color: '',
      vehicle_type: 'Van',
      fuel_type: 'Diesel',
      mot_expiry: '',
      tax_expiry: '',
      service_due_date: '',
      service_due_mileage: '',
      current_mileage: '',
      allocated_date: new Date().toISOString().split('T')[0],
      return_date: '',
      insurance_policy: '',
      insurance_expiry: '',
      status: 'Active',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      registration: vehicle.registration || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      color: vehicle.color || '',
      vehicle_type: vehicle.vehicle_type || 'Van',
      fuel_type: vehicle.fuel_type || 'Diesel',
      mot_expiry: vehicle.mot_expiry ? vehicle.mot_expiry.split('T')[0] : '',
      tax_expiry: vehicle.tax_expiry ? vehicle.tax_expiry.split('T')[0] : '',
      service_due_date: vehicle.service_due_date ? vehicle.service_due_date.split('T')[0] : '',
      service_due_mileage: vehicle.service_due_mileage || '',
      current_mileage: vehicle.current_mileage || '',
      allocated_date: vehicle.allocated_date ? vehicle.allocated_date.split('T')[0] : '',
      return_date: vehicle.return_date ? vehicle.return_date.split('T')[0] : '',
      insurance_policy: vehicle.insurance_policy || '',
      insurance_expiry: vehicle.insurance_expiry ? vehicle.insurance_expiry.split('T')[0] : '',
      status: vehicle.status || 'Active',
      notes: vehicle.notes || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.registration) {
      alert('Registration is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingVehicle
        ? `/api/employees/vehicles/${editingVehicle.id}`
        : '/api/employees/vehicles';

      const response = await apiFetch(url, {
        method: editingVehicle ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          employee_id: employeeId
        })
      });

      if (response.ok) {
        setShowModal(false);
        onRefresh();
      } else {
        alert('Error saving vehicle');
      }
    } catch (error) {
      console.error('Error saving vehicle:', error);
      alert('Error saving vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, registration) => {
    if (!window.confirm(`Delete vehicle "${registration}"?`)) return;

    try {
      await apiFetch(`/api/employees/vehicles/${id}`, {
        method: 'DELETE'
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert('Error deleting vehicle');
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Company Vehicles</h2>
          <button 
            onClick={handleAdd}
            className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded transition-colors"
          >
            + Assign Vehicle
          </button>
        </div>

      {vehicles.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No vehicles assigned</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-300">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('registration')}
                >
                  Registration{getSortIndicator('registration')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('make')}
                >
                  Make / Model{getSortIndicator('make')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('vehicle_type')}
                >
                  Type{getSortIndicator('vehicle_type')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('mot_expiry')}
                >
                  MOT Expiry{getSortIndicator('mot_expiry')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('service_due_date')}
                >
                  Service Due{getSortIndicator('service_due_date')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('status')}
                >
                  Status{getSortIndicator('status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedVehicles.map((vehicle, idx) => (
                <tr key={vehicle.id} className={idx % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{vehicle.registration}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{vehicle.make} {vehicle.model}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{vehicle.vehicle_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(vehicle.mot_expiry)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(vehicle.service_due_date)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      vehicle.status === 'Active' ? 'bg-green-100 text-green-800' :
                      vehicle.status === 'In Service' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {vehicle.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm space-x-2">
                    <button
                      onClick={() => handleEdit(vehicle)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(vehicle.id, vehicle.registration)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* Add/Edit Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-slate-600 text-white px-6 py-4 rounded-t-lg">
            <h3 className="text-xl font-semibold">
              {editingVehicle ? 'Edit Vehicle' : 'Assign Vehicle'}
            </h3>
          </div>

          <div className="p-6 space-y-6">
            {/* Vehicle Details */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Vehicle Details</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration *</label>
                  <input
                    type="text"
                    value={formData.registration}
                    onChange={(e) => setFormData({...formData, registration: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                    placeholder="AB12 CDE"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                  <input
                    type="text"
                    value={formData.make}
                    onChange={(e) => setFormData({...formData, make: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                  <select
                    value={formData.vehicle_type}
                    onChange={(e) => setFormData({...formData, vehicle_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  >
                    <option value="Van">Van</option>
                    <option value="Car">Car</option>
                    <option value="Truck">Truck</option>
                    <option value="Motorcycle">Motorcycle</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Type</label>
                  <select
                    value={formData.fuel_type}
                    onChange={(e) => setFormData({...formData, fuel_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  >
                    <option value="Diesel">Diesel</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Mileage</label>
                  <input
                    type="number"
                    value={formData.current_mileage}
                    onChange={(e) => setFormData({...formData, current_mileage: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  >
                    <option value="Active">Active</option>
                    <option value="In Service">In Service</option>
                    <option value="Off Road">Off Road</option>
                    <option value="Sold">Sold</option>
                  </select>
                </div>
              </div>
            </div>

            {/* MOT & Service */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">MOT & Service</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MOT Expiry</label>
                  <input
                    type="date"
                    value={formData.mot_expiry}
                    onChange={(e) => setFormData({...formData, mot_expiry: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Expiry</label>
                  <input
                    type="date"
                    value={formData.tax_expiry}
                    onChange={(e) => setFormData({...formData, tax_expiry: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Due Date</label>
                  <input
                    type="date"
                    value={formData.service_due_date}
                    onChange={(e) => setFormData({...formData, service_due_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Due Mileage</label>
                  <input
                    type="number"
                    value={formData.service_due_mileage}
                    onChange={(e) => setFormData({...formData, service_due_mileage: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>
              </div>
            </div>

            {/* Allocation & Insurance */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Allocation & Insurance</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allocated Date</label>
                  <input
                    type="date"
                    value={formData.allocated_date}
                    onChange={(e) => setFormData({...formData, allocated_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
                  <input
                    type="date"
                    value={formData.return_date}
                    onChange={(e) => setFormData({...formData, return_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Policy</label>
                  <input
                    type="text"
                    value={formData.insurance_policy}
                    onChange={(e) => setFormData({...formData, insurance_policy: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Expiry</label>
                  <input
                    type="date"
                    value={formData.insurance_expiry}
                    onChange={(e) => setFormData({...formData, insurance_expiry: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded disabled:opacity-50"
            >
              {saving ? 'Saving...' : (editingVehicle ? 'Update' : 'Assign Vehicle')}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}

export default EmployeeVehiclesTab;
